(() => {
  const desktopMedia = window.matchMedia('(min-width: 1025px)');
  const switcherSelector = '.weglot-container, .country-selector, .wg-default, .wg-drop';
  const targetSelector = '[data-weglot-desktop-target], [data-weglot-mobile-target]';
  const codeSelector = '.wg-code, .wg-lcode, .wg_code, .wgcode';
  const refreshEvents = [
    'cart:updated',
    'cart:refresh',
    'cart-drawer:open',
    'predictive-search:updated',
    'quick-add:open',
    'shopify:section:load'
  ];
  const languageNames = {
    en: 'English',
    es: 'Español',
    de: 'Deutsch',
    fr: 'Français'
  };
  const marketCountryByHostname = {
    'gemilai.com': 'US',
    'www.gemilai.com': 'US',
    'es.gemilai.com': 'ES',
    'de.gemilai.com': 'DE',
    'fr.gemilai.com': 'FR'
  };
  const languageDomainByCode = {
    en: 'www.gemilai.com',
    es: 'es.gemilai.com',
    de: 'de.gemilai.com',
    fr: 'fr.gemilai.com'
  };
  const dirtyQueryParameters = ['shpxid', 'locale', 'currency'];
  const marketSyncStoragePrefix = 'gemilai-market-sync';
  let mountedSwitcher = null;
  let scheduled = false;
  let refreshScheduled = false;
  let refreshInProgress = false;
  let mutationRefreshTimer = null;

  const isElement = (node) => node instanceof HTMLElement;

  const isInsideTarget = (node) => Boolean(node.closest(targetSelector));

  const isNestedSwitcher = (node) => Boolean(node.parentElement?.closest(switcherSelector));

  const isSwitcher = (node) => {
    if (!isElement(node)) return false;
    if (!node.matches(switcherSelector)) return false;
    if (node.matches(targetSelector)) return false;
    if (node.matches('[data-weglot-placeholder]')) return false;
    if (isNestedSwitcher(node)) return false;
    return true;
  };

  const switchers = () => Array.from(document.querySelectorAll(switcherSelector)).filter(isSwitcher);

  const currentTarget = () => document.querySelector(desktopMedia.matches ? '[data-weglot-desktop-target]' : '[data-weglot-mobile-target]');

  const cleanUrl = () => {
    const url = new URL(window.location.href);
    url.pathname = url.pathname.replace(/^\/(en|es|de|fr)(?=\/|$)/i, '') || '/';
    dirtyQueryParameters.forEach((parameter) => {
      url.searchParams.delete(parameter);
    });
    return url;
  };

  const languageTargetUrl = (language) => {
    const hostname = languageDomainByCode[language];
    if (!hostname) return null;
    const url = cleanUrl();
    url.protocol = 'https:';
    url.hostname = hostname;
    url.port = '';
    return url;
  };

  const currentMarketCountry = () => String(window.__SHOPIFY_CURRENT_COUNTRY__ || '').toUpperCase();

  const targetMarketCountry = () => marketCountryByHostname[window.location.hostname.toLowerCase()] || '';

  const targetCountryIsAvailable = (country) => {
    const availableCountries = window.__SHOPIFY_AVAILABLE_COUNTRIES__;
    if (!Array.isArray(availableCountries) || availableCountries.length === 0) return true;
    return availableCountries.map((code) => String(code).toUpperCase()).includes(country);
  };

  const buildLocalizationForm = (country, returnTo) => {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = '/localization';
    form.hidden = true;
    form.style.setProperty('display', 'none', 'important');
    [
      ['form_type', 'localization'],
      ['utf8', '✓'],
      ['_method', 'put'],
      ['country_code', country],
      ['return_to', returnTo]
    ].forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    return form;
  };

  const syncMarketToHostname = () => {
    const country = targetMarketCountry();
    if (!country || !document.body || !targetCountryIsAvailable(country)) return;

    const cleanedUrl = cleanUrl();
    const returnTo = `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`;
    const currentCountry = currentMarketCountry();
    const syncKey = `${marketSyncStoragePrefix}:${window.location.hostname}:${country}:${returnTo}`;

    if (currentCountry === country) {
      sessionStorage.removeItem(syncKey);
      if (cleanedUrl.href !== window.location.href) {
        window.location.replace(cleanedUrl.href);
      }
      return;
    }

    if (sessionStorage.getItem(syncKey)) return;

    sessionStorage.setItem(syncKey, '1');
    const form = buildLocalizationForm(country, returnTo);
    document.body.appendChild(form);
    form.submit();
  };

  const scheduleMarketSync = () => {
    window.setTimeout(syncMarketToHostname, 0);
  };

  const languageCodeFor = (node) => {
    if (!isElement(node)) return '';
    const option = node.closest('[data-l], [data-value], [lang], .language-option, a') || node;
    const code = option.getAttribute('data-language-code')
      || option.getAttribute('data-l')
      || option.getAttribute('data-value')
      || option.getAttribute('lang')
      || option.id.replace(/^weglot-language-/, '')
      || option.id.replace(/^wg-/, '')
      || option.querySelector('[data-l]')?.getAttribute('data-l')
      || option.querySelector('a[lang]')?.getAttribute('lang')
      || option.querySelector('[lang]')?.getAttribute('lang')
      || '';
    return String(code || '').toLowerCase().replace('_', '-').split('-')[0];
  };

  const handleLanguageClick = (event) => {
    const clickedElement = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!clickedElement || !clickedElement.closest(switcherSelector)) return;

    const languageOption = clickedElement.closest('[data-l], [data-value], [lang], .language-option, a');
    const language = languageCodeFor(languageOption || clickedElement);
    const targetUrl = languageTargetUrl(language);
    if (!targetUrl) return;

    const cleanCurrentUrl = cleanUrl();
    if (targetUrl.href === cleanCurrentUrl.href && window.location.href === cleanCurrentUrl.href) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(targetUrl.href);
  };

  const showSwitcher = (node) => {
    node.removeAttribute('data-weglot-hidden-duplicate');
    node.removeAttribute('aria-hidden');
    node.hidden = false;
    node.style.removeProperty('display');
    node.style.removeProperty('visibility');
    node.style.removeProperty('pointer-events');
  };

  const hideDuplicateSwitchers = (keep) => {
    switchers().forEach((node) => {
      if (node === keep) {
        showSwitcher(node);
        return;
      }
      node.setAttribute('data-weglot-hidden-duplicate', '');
      node.setAttribute('aria-hidden', 'true');
      node.hidden = true;
      node.style.setProperty('display', 'none', 'important');
      node.style.setProperty('visibility', 'hidden', 'important');
      node.style.setProperty('pointer-events', 'none', 'important');
    });
  };

  const removeLanguageCodes = (root) => {
    if (!isElement(root)) return;
    root.querySelectorAll(codeSelector).forEach((node) => {
      node.setAttribute('aria-hidden', 'true');
      node.hidden = true;
      node.style.setProperty('display', 'none', 'important');
    });
    root.querySelectorAll('.wgcurrent, .language-option, a, li, span, button').forEach((node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType !== Node.TEXT_NODE) return;
        const original = child.textContent || '';
        const cleaned = original.replace(/(^|\s)(US|ES|DE|FR|EN)(?=\s|$)/gi, ' ').replace(/\s{2,}/g, ' ');
        if (cleaned.trim() !== original.trim()) {
          child.textContent = cleaned.trim() ? cleaned : '';
        }
      });
    });
  };

  const ensureLanguageLabels = (root) => {
    if (!isElement(root)) return;
    root.querySelectorAll('.language-option').forEach((option) => {
      const link = option.querySelector('a');
      if (!link || link.textContent.trim()) return;
      const language = languageCodeFor(option);
      const fallback = languageNames[language];
      if (fallback) link.append(document.createTextNode(fallback));
    });
  };

  const hideShopifyHeaderLocalization = () => {
    document.querySelectorAll([
      'header localization-form',
      'header form.localization-form',
      'header .localization-form',
      '.menu-drawer localization-form',
      '.menu-drawer form.localization-form',
      '.menu-drawer .localization-form',
      '.header__action localization-form',
      '.header__action form.localization-form',
      '.header__action .localization-form'
    ].join(',')).forEach((node) => {
      if (!isElement(node)) return;
      node.setAttribute('aria-hidden', 'true');
      node.hidden = true;
      node.style.setProperty('display', 'none', 'important');
    });
  };

  const selectSwitcher = () => {
    if (mountedSwitcher && document.documentElement.contains(mountedSwitcher)) return mountedSwitcher;
    const nodes = switchers().filter((node) => !node.hasAttribute('data-weglot-hidden-duplicate'));
    return nodes.find(isInsideTarget) || nodes[0] || null;
  };

  const mount = () => {
    scheduled = false;
    hideShopifyHeaderLocalization();
    const target = currentTarget();
    mountedSwitcher = selectSwitcher();
    if (!target || !mountedSwitcher) {
      hideDuplicateSwitchers(mountedSwitcher);
      return;
    }
    if (mountedSwitcher.parentNode !== target) {
      target.appendChild(mountedSwitcher);
    }
    showSwitcher(mountedSwitcher);
    ensureLanguageLabels(mountedSwitcher);
    removeLanguageCodes(mountedSwitcher);
    hideDuplicateSwitchers(mountedSwitcher);
  };

  const scheduleMount = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(mount);
  };

  const refreshWeglotTranslations = () => {
    if (refreshScheduled) return;
    refreshScheduled = true;
    window.setTimeout(() => {
      refreshScheduled = false;
      if (!window.Weglot) return;
      refreshInProgress = true;
      if (typeof window.Weglot.translatePage === 'function') {
        window.Weglot.translatePage();
      }
      if (typeof window.Weglot.translateElements === 'function') {
        window.Weglot.translateElements(document.body);
      }
      window.setTimeout(() => {
        refreshInProgress = false;
      }, 1000);
    }, 250);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleMount, { once: true });
    document.addEventListener('DOMContentLoaded', scheduleMarketSync, { once: true });
  } else {
    scheduleMount();
    scheduleMarketSync();
  }

  refreshEvents.forEach((eventName) => {
    document.addEventListener(eventName, refreshWeglotTranslations);
  });

  document.addEventListener('click', handleLanguageClick, true);

  [250, 750, 1500, 3000].forEach((delay) => {
    window.setTimeout(scheduleMount, delay);
  });

  desktopMedia.addEventListener('change', scheduleMount);

  if (window.Weglot && typeof window.Weglot.on === 'function') {
    window.Weglot.on('initialized', scheduleMount);
    window.Weglot.on('languageChanged', refreshWeglotTranslations);
  }

  const observer = new MutationObserver(() => {
    scheduleMount();
    if (refreshInProgress) return;
    window.clearTimeout(mutationRefreshTimer);
    mutationRefreshTimer = window.setTimeout(refreshWeglotTranslations, 600);
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
